import React, { useEffect, useState } from "react";
import { useAppStore } from "../../../../../context/store";
import { Dialog, Portal, SimpleGrid, Select, HStack, CloseButton, Button, createListCollection, Field, Input, Separator } from "@chakra-ui/react";
import { resetEntityFilter } from "../../../../../utilies/Utility";
import { LuX } from "react-icons/lu";
import { applyGeojsonLayerFilters, clearLayerFilters, filterGeojsonEntitiesByProperty } from "../../../../../utilies/filter/GeojsonFilter";
import { useMapObjects } from "../../../../../context/MapObjectsContext";

const extractNestedKeys = (obj, maxDepth = 3, prefix = "") => {
  const result = [];
  if (!obj || typeof obj !== "object") return result;

  if (Array.isArray(obj)) {
    const firstObj = obj.find((el) => el && typeof el === "object");
    if (!firstObj) return result;
    return extractNestedKeys(firstObj, maxDepth, prefix);
  }

  const keys = Object.keys(obj);

  for (const key of keys) {
    const path = prefix ? `${prefix}.${key}` : key;
    result.push(path);

    if (maxDepth > 1) {
      const value = obj[key];
      if (value && typeof value === "object") {
        result.push(...extractNestedKeys(value, maxDepth - 1, path));
      }
    }
  }

  return result;
};

export const GeojsonFilterDialog = () => {
  const filterGeojsonDialog = useAppStore((s) => s.filterGeojsonDialog);
  const currentLayer = useAppStore((s) => s.currentLayer);
  const { mapObjects, setMapObjects } = useMapObjects();

  const setFilterGeojsonDialog = useAppStore.getState().setFilterGeojsonDialog;
  const setCurrentLayer = useAppStore.getState().setCurrentLayer;
  const viewerRef = useAppStore.getState().viewerRef;

  const EMPTY_FILTER = {
    key: "",
    operation: "==",
    value: "",
    logicalOp: "",
  };

  const [labelPropertiesOption, setLabelPropertiesOption] = useState([]);
  const operationOption = [{ name: "==" }, { name: "!=" }, { name: "<" }, { name: "<=" }, { name: ">" }, { name: ">=" }];
  const logicalOperationOption = [{ name: "AND" }, { name: "OR" }];
  const [filters, setFilters] = useState([{ ...EMPTY_FILTER }]);
  useEffect(() => {
    if (filterGeojsonDialog && currentLayer) {
      const ds = currentLayer?.dataSource;

      let temp = [];

      if (ds && ds.entities && ds.entities.values && ds.entities.values.length > 0) {
        const firstEntity = ds.entities.values[0];
        const props = firstEntity && firstEntity.properties;

        let nestedKeys = [];

        if (props && typeof props === "object") {
          // get top-level property names from Cesium PropertyBag
          const propNames = (props.propertyNames && Array.isArray(props.propertyNames) && props.propertyNames) || Object.keys(props || {});

          propNames.forEach((name) => {
            // always include root key
            nestedKeys.push(name);

            let prop = props[name];
            let value = prop;

            // handle Cesium Property (ConstantProperty, etc.)
            if (prop && typeof prop.getValue === "function") {
              try {
                const time = viewerRef?.current?.clock?.currentTime;
                value = prop.getValue(time);
              } catch (e) {
                try {
                  value = prop.getValue();
                } catch {
                  value = prop;
                }
              }
            }

            // if value is plain object, extract nested keys with dot-notation
            if (value && typeof value === "object") {
              nestedKeys.push(...extractNestedKeys(value, 4, name));
            }
          });
        }

        if (!nestedKeys || nestedKeys.length === 0) {
          const propNames = (props && props.propertyNames) || Object.keys(props || {});
          nestedKeys = propNames || [];
        }

        const uniqueKeys = Array.from(new Set(nestedKeys));
        temp = uniqueKeys.map((name) => ({ name }));

        setLabelPropertiesOption(temp);
      } else {
        setLabelPropertiesOption([]);
      }

      if (Array.isArray(currentLayer.filters) && currentLayer.filters.length > 0) {
        const normalized = currentLayer.filters.map((f, index) => {
          return {
            ...EMPTY_FILTER,
            ...f,
            key: f.key || f.key2 || f.key1 || "",
            operation: f.operation || "==",
            value: f.value || "",
            logicalOp: f.logicalOp ?? (index === 0 ? "" : "AND"),
          };
        });
        setFilters(normalized);
      } else {
        const idItem = temp.find((it) => it.name.toLowerCase() === "id");
        setFilters([
          {
            ...EMPTY_FILTER,
            key: idItem ? idItem.name : "",
          },
        ]);
      }
    }
  }, [currentLayer, filterGeojsonDialog, viewerRef]);

  const updateFilter = (index, field, newValue) => {
    const updated = [...filters];
    updated[index][field] = newValue;
    setFilters(updated);
  };

  const addFilter = (logicalOp) => {
    setFilters([
      ...filters,
      {
        ...EMPTY_FILTER,
        logicalOp: logicalOp || "AND",
      },
    ]);
  };

  const removeFilter = (index) => {
    const updated = filters.filter((_, i) => i !== index);
    setFilters(updated.length > 0 ? updated : [{ ...EMPTY_FILTER }]);
  };

  const handleFilter = () => {
    applyGeojsonLayerFilters({
      viewer: viewerRef.current,
      dataSource: currentLayer.dataSource,
      filters,
      layerId: currentLayer.id,
      setMapObjects,
      setFilterGeojsonDialog,
      closeDialog: true,
    });
  };

  const logicalOperationCollection = React.useMemo(
    () =>
      createListCollection({
        items: logicalOperationOption,
        itemToString: (item) => item.name,
        itemToValue: (item) => item.name,
      }),
    [logicalOperationOption],
  );

  const keyCollection = React.useMemo(
    () =>
      createListCollection({
        items: labelPropertiesOption,
        itemToString: (item) => item.name,
        itemToValue: (item) => item.name,
      }),
    [labelPropertiesOption],
  );

  const operationCollection = React.useMemo(
    () =>
      createListCollection({
        items: operationOption,
        itemToString: (item) => item.name,
        itemToValue: (item) => item.name,
      }),
    [operationOption],
  );

  return (
    <Dialog.Root
      lazyMount
      open={filterGeojsonDialog}
      closeOnInteractOutside={false}
      onOpenChange={(e) => {
        setFilterGeojsonDialog(e.open);
        if (!Boolean(e.open)) {
          setCurrentLayer(null);
        }
      }}
      trapFocus={false}
      restoreFocus={false}
      initialFocusEl={() => null}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Filter</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body pb={3}>
              {filters.map((filter, index) => (
                <SimpleGrid columns={5} gap={2} key={index} alignItems="end" templateColumns="80px 2fr 1.2fr 2fr 40px">
                  {/* Logical Op */}
                  {index === 0 ? (
                    <HStack justifyContent="center" fontSize="sm">
                      <span>—</span>
                    </HStack>
                  ) : (
                    <Select.Root
                      collection={logicalOperationCollection}
                      size="sm"
                      value={[filter.logicalOp || "AND"]}
                      positioning={{ sameWidth: false }}
                      onValueChange={(value) => updateFilter(index, "logicalOp", value.value[0])}
                    >
                      <Select.HiddenSelect />
                      <Select.Control w="100%">
                        <Select.Trigger w="100%">
                          <HStack w="100%">{filter.logicalOp || "AND"}</HStack>
                        </Select.Trigger>
                        <Select.IndicatorGroup>
                          <Select.Indicator />
                        </Select.IndicatorGroup>
                      </Select.Control>
                      <Select.Positioner>
                        <Select.Content minW="160px" maxH="200px" overflowY="auto">
                          {logicalOperationOption.map((item) => (
                            <Select.Item key={item.name} item={item}>
                              {item.name}
                              <Select.ItemIndicator />
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Positioner>
                    </Select.Root>
                  )}

                  {/* Key */}
                  <Select.Root collection={keyCollection} size="sm" value={[filter.key]} positioning={{ sameWidth: false }} onValueChange={(value) => updateFilter(index, "key", value.value[0])}>
                    <Select.HiddenSelect />
                    <Select.Control w="100%">
                      <Select.Trigger w="100%">
                        <HStack w="100%">{filter.key || "Key"}</HStack>
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.Indicator />
                      </Select.IndicatorGroup>
                    </Select.Control>
                    <Select.Positioner>
                      <Select.Content minW="220px" maxH="240px" overflowY="auto">
                        {labelPropertiesOption.map((item) => (
                          <Select.Item key={item.name} item={item}>
                            {item.name}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>

                  {/* Operation */}
                  <Select.Root
                    collection={operationCollection}
                    size="sm"
                    value={[filter.operation]}
                    positioning={{ sameWidth: false }}
                    onValueChange={(value) => updateFilter(index, "operation", value.value[0])}
                  >
                    <Select.HiddenSelect />
                    <Select.Control w="100%">
                      <Select.Trigger w="100%">
                        <HStack w="100%">{filter.operation}</HStack>
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.Indicator />
                      </Select.IndicatorGroup>
                    </Select.Control>
                    <Select.Positioner>
                      <Select.Content minW="120px">
                        {operationOption.map((item) => (
                          <Select.Item key={item.name} item={item}>
                            {item.name}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>

                  {/* Value */}
                  <Field.Root>
                    <Input height="36px" placeholder="Value" value={filter.value} onChange={(e) => updateFilter(index, "value", e.target.value)} />
                  </Field.Root>

                  {/* Remove */}
                  <Button size="sm" variant="ghost" mt={4} onClick={() => removeFilter(index)}>
                    <LuX />
                  </Button>
                </SimpleGrid>
              ))}
              <Separator mt={5} />
            </Dialog.Body>

            <Dialog.Footer justifyContent={"space-between"}>
              <HStack gap={1}>
                <Button p={0} borderRadius="full" onClick={() => addFilter("AND")} textAlign={"center"}>
                  AND
                </Button>
                <Button p={0} borderRadius="full" onClick={() => addFilter("OR")} textAlign={"center"}>
                  OR
                </Button>
              </HStack>
              <HStack gap={2}>
                <Button
                  onClick={() => {
                    clearLayerFilters(currentLayer, mapObjects, setMapObjects, resetEntityFilter);
                  }}
                  colorPalette={"red"}
                >
                  Remove Filters
                </Button>
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline">Cancel</Button>
                </Dialog.ActionTrigger>
                <Button onClick={handleFilter}>Filter</Button>
              </HStack>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default GeojsonFilterDialog;
